import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { QueryParamsModels } from "../models/APIModels";
import { useCallback, useContext, useMemo, useRef } from "react";
import { AlertColor } from "@mui/material";
import { SpinnerContext } from "../../SpinnerContext";
import { useNavigate } from "react-router-dom";
import { AuthenticationContext } from "../../../shared/Contexts";
import { AlertBarContext } from "../../AlertBarContext";
import { OPTIVAL_CONSTANTS } from "../../Constants";
import { useTranslation } from "react-i18next";

export const useAPI = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authenticate = useContext(AuthenticationContext);
  const { toggleSpinner } = useContext(SpinnerContext);
  const { showAlertBar } = useContext(AlertBarContext);
  const { userRole } = useContext(AuthenticationContext);

  const toggleSpinnerRef = useRef(toggleSpinner);
  toggleSpinnerRef.current = toggleSpinner;
  const showAlertBarRef = useRef(showAlertBar);
  showAlertBarRef.current = showAlertBar;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const authenticateRef = useRef(authenticate);
  authenticateRef.current = authenticate;
  const tRef = useRef(t);
  tRef.current = t;

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_OPVA_API_BASE_URL,
    });

    const apiErrorMessage = (response: {
      data?: { message?: string; code?: string };
    }) => {
      const data = response?.data;
      if (data?.message) {
        return data.message;
      }
      if (data?.code) {
        const translated = tRef.current(`opva.${data.code}`);
        if (translated && translated !== `opva.${data.code}`) {
          return translated;
        }
        return data.code;
      }
      return OPTIVAL_CONSTANTS.BAD_REQUEST;
    };

    const handleLogout = () => {
      localStorage.removeItem("jwtToken");
      sessionStorage.clear();
      localStorage.clear();
      authenticateRef.current.updateToken("");
      navigateRef.current("/");
    };

    instance.interceptors.request.use(
      (config) => {
        if (config.headers["isLoading"]) {
          toggleSpinnerRef.current(true);
        }
        return config;
      },
      (error: AxiosError) => {
        toggleSpinnerRef.current(false);
        console.error("API Request Error->", error);
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      function (response: any) {
        toggleSpinnerRef.current(false);
        if (response && response.data && response.data.error) {
          if (response.data.status && response.data.status === 400) {
            const message = apiErrorMessage(response);
            showAlertBarRef.current({
              open: true,
              type: "error",
              message,
            });
            throw new Error(message);
          } else if (
            response.data.status &&
            response.data.code === "LICENSE_EXPIRED"
          ) {
            showAlertBarRef.current({
              open: true,
              type: "info",
              message: OPTIVAL_CONSTANTS.LICENSE_EXPIRED,
            });
            handleLogout();
            throw new Error(OPTIVAL_CONSTANTS.LICENSE_EXPIRED);
          } else if (response.data.status && response.data.status === 401) {
            showAlertBarRef.current({
              open: true,
              type: "info",
              message: OPTIVAL_CONSTANTS.USER_SESSION_TIMEOUT,
            });
            handleLogout();
            throw new Error(OPTIVAL_CONSTANTS.USER_SESSION_TIMEOUT);
          } else if (response.data.status && response.data.status === 404) {
            showAlertBarRef.current({
              open: true,
              type: "error",
              message: OPTIVAL_CONSTANTS.SERVICE_NOT_FOUND,
            });
            throw new Error(OPTIVAL_CONSTANTS.SERVICE_NOT_FOUND);
          } else if (response.data.status === 409) {
            return response;
          }
          const message = apiErrorMessage(response);
          showAlertBarRef.current({
            open: true,
            type: "error",
            message,
          });
          throw new Error(message);
        }
        return response;
      },
      function (error: AxiosError) {
        console.error("API Response Error->", error);
        showAlertBarRef.current({
          open: true,
          type: "error",
          message: tRef.current("opva.serviceUnavailable"),
        });
        toggleSpinnerRef.current(false);
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  const getRequestConfig = useCallback(
    (
      params: QueryParamsModels | undefined,
      isSpinner: boolean | undefined = true,
      data?: any
    ): AxiosRequestConfig => {
      const jwtToken = localStorage.getItem("jwtToken");
      return {
        validateStatus: () => true,
        params,
        headers: {
          ...(jwtToken ? { Authorization: jwtToken } : {}),
          isLoading: isSpinner ? "BackDrop" : null,
        },
        data: data,
      };
    },
    []
  );

  const httpGet = useCallback(
    (url: string, params?: QueryParamsModels, isSpinner?: boolean) => {
      return axiosInstance.get(url, getRequestConfig(params, isSpinner));
    },
    [axiosInstance, getRequestConfig]
  );

  const httpPost = useCallback(
    (
      url: string,
      data: any,
      params?: QueryParamsModels,
      isSpinner?: boolean
    ) => {
      return axiosInstance.post(url, data, getRequestConfig(params, isSpinner));
    },
    [axiosInstance, getRequestConfig]
  );

  const httpPostFormData = useCallback(
    (url: string, formData: FormData, isSpinner?: boolean) => {
      return axiosInstance.post(
        url,
        formData,
        getRequestConfig(undefined, isSpinner)
      );
    },
    [axiosInstance, getRequestConfig]
  );

  const httpPut = useCallback(
    (
      url: string,
      data: any,
      params?: QueryParamsModels,
      isSpinner?: boolean
    ) => {
      return axiosInstance.put(url, data, getRequestConfig(params, isSpinner));
    },
    [axiosInstance, getRequestConfig]
  );

  const httpDelete = useCallback(
    (
      url: string,
      data?: any,
      params?: QueryParamsModels,
      isSpinner?: boolean
    ) => {
      return axiosInstance.delete(
        url,
        getRequestConfig(params, isSpinner, data)
      );
    },
    [axiosInstance, getRequestConfig]
  );

  const handleAlertBar = useCallback(
    (type: AlertColor, message: string, title?: string) => {
      showAlertBarRef.current({
        open: true,
        type,
        message,
        title,
      });
    },
    []
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem("jwtToken");
    sessionStorage.clear();
    localStorage.clear();
    authenticateRef.current.updateToken("");
    navigateRef.current("/");
  }, []);

  const checkRole = useCallback(
    (roles: string[]): boolean => {
      return !!userRole.filter((element) => roles.includes(element)).length;
    },
    [userRole]
  );

  return {
    httpGet,
    httpPost,
    httpPostFormData,
    httpPut,
    httpDelete,
    handleAlertBar,
    checkRole,
  };
};
